<template>
  <div>
    <portal to="search">
      <div class="flex items-center sm:px-2 w-full divide-x divide-gray-200 gap-x-3">
        <h1 class="font-bold text-xl">Dispatch Notes</h1>
        <QuickSearchOptions :filters="form" @apply="applyFilter" />
      </div>
    </portal>
    <portal to="quickActions">
      <div class="sm:space-x-3 flex items-center">
        <NoteFilterModal
          v-model="form"
          :vessels="vessels"
          :locations="locations"
          :warehouses="warehouses"
          :teams="teams"
          :filters="filters"
          @apply="applyFilter"
          @reset="reset"
        >
          <template #trigger="{open}">
            <button @click="open" class="btn-primary py-2 h-8 px-3">
              <Icon name="funnel" class="h-4 w-4" />
            </button>
          </template>
        </NoteFilterModal>
        <div v-if="hasFilter">
          <button @click="reset" class="py-2 text-sm">
            <Icon name="xmark" class="h-6 w-6" />
          </button>
        </div>
      </div>
    </portal>
    <div class="flex justify-between items-center w-full py-4 sm:py-0">
      <div class="flex gap-x-5">

        <StatusToggle :status="form.status" @toggle="toggleStatus" />
        <DeliveryTypeToggle :type="form.method" @toggle="toggleType" />
      </div>
        <inertia-link :href="route('delivery-notes.schedules')" class="text-blue-500 hover:underline">Delivery Schedules</inertia-link>
    </div>

    <div class="md:mt-5">
      <div v-if="notes.data && notes.data.length === 0" class="text-center py-4 text-sm text-gray-400 italic">
        No items found for the selected criteria
      </div>
      <div v-else class="space-y-5">
        <div class="">
          <div class="flow-root">
            <div class="overflow-x-auto">
              <div class="inline-block min-w-full py-2 align-middle sm:p-1">
                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table class="min-w-full divide-y divide-gray-300">
                    <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Note #</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Order #</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Customer</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Total QTY</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Team</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Items Collected at</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Delivery Date</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Delivery Location</th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Vessel/Address
                      </th>
                      <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                    </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                    <template v-for="note in notes.data">
                      <tr @click="toggleNote(note)" :key="'note-'+note.id" class="cursor-pointer group" :class="{'bg-gray-50': hasNoteExpended(note)}">
                        <td class="group-hover:bg-gray-50 whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          #{{ note.id }}
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap text-sm text-gray-500 capitalize">
                          <a v-if="canAdmin('access-orders')" :href="route('customer.orders.show', note.order_id)"
                             target="_blank"
                             class="text-blue-500 hover:underline px-3 py-2"
                          >
                            {{ note.order_id }}
                          </a>
                          <span v-else>{{ note.order_id }}</span>
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap text-sm text-gray-500 capitalize">
                          <template v-if="note.customer_account">
                            {{ note.customer_account.name }}
                            <div>{{ note.customer_account.tin_number }}</div>
                          </template>
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          {{ note.total_qty || 0 }}
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          {{ note.type === 'collection' ? 'Collection by Customer' : 'To be Delivered' }}
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-pre-line px-3 py-2 text-sm">
                          {{ deliveryTeams(note.delivery_teams) }}
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                          <template v-if="note.type === 'delivery' && note.goods_collected_for_delivery_at">
                            {{ note.goods_collected_for_delivery_at | localDateTime }}
                          </template>
                          <template v-else-if="note.type === 'collection'">
                            <template v-if="note.status === 'completed'">
                              {{ note.customer_goods_collected_at | localDateTime }}
                            </template>
                            <template v-else>
                              Pending Collection
                            </template>
                          </template>
                          <template v-else>
                            ---
                          </template>
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                          {{ note.delivery_date | localDate }}
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          <template v-if="note.type === 'collection'">
                            N/A
                          </template>
                          <template v-else>
                            {{ note.delivery_location }}
                          </template>
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                          <template v-if="note.delivery_location === 'island'">
                            {{ note.vessel }}
                          </template>
                          <template v-else-if="note.delivery_address">
                            {{ note.delivery_address }}
                          </template>
                          <template v-else>N/A</template>
                        </td>
                        <td class="group-hover:bg-gray-50 whitespace-nowrap px-3 py-2 text-sm text-gray-500 capitalize">
                            <Badge
                              class="px-3 py-1.5 rounded-lg capitalize"
                              :variant="
                              note.status === 'completed'
                              ? 'green'
                              : note.status === 'in-progress'
                              ? 'blue'
                              : note.status === 'void'
                              ? 'red'
                              : 'gray'
                            "
                            >
                              {{ note.status }}
                            </Badge>
                        </td>
                      </tr>
                      <transition
                        enter-active-class="transition ease-out duration-100 delay-100"
                        enter-class="transform opacity-0"
                        enter-to-class="transform opacity-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-class="transform opacity-100"
                        leave-to-class="transform opacity-0"
                      >
                        <tr v-if="hasNoteExpended(note)">
                          <td colspan="11" class="bg-gray-200">
                            <MasterList
                              :note="note"
                              :warehouses="warehouses"
                              :locations="locations"
                              :vessels="vessels"
                              :vesselLocations="vesselLocations"
                              :filters="filters"
                              items-route="ajax.delivery-notes.get-master-items"
                              :is-admin="isAdmin"
                            />
                          </td>
                        </tr>
                      </transition>
                    </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <pagination :links="notes.links"></pagination>
  </div>
</template>

<script>
import Layout from "@/Shared/Admin/Layout.vue";
import Pagination from "../../../Shared/Pagination.vue";
import SelectInput from "../../../Shared/SelectInput.vue";
import DateInput from "../../../Shared/DateInput.vue";
import TextInput from "../../../Shared/TextInput.vue";
import {mapValues, debounce, pickBy} from "lodash";
import Badge from "../../../Shared/Badge.vue";
import SearchFilter from "../../../Shared/SearchFilter.vue";
import NoteFilterModal from "../Dispatch/Partials/NoteFilterModal.vue";
import Icon from "../../../Shared/Icon.vue";
import QuickSearchOptions from "../Dispatch/Partials/QuickSearchOptions.vue";
import StatusToggle from "../Dispatch/Partials/StatusToggle.vue";
import DeliveryTypeToggle from "../Dispatch/Partials/DeliveryTypeToggle.vue";
import MasterList from "./MasterList.vue";

export default {
  name: 'ListIndex',
  components: {
    MasterList,
    DeliveryTypeToggle,
    StatusToggle,
    QuickSearchOptions,
    Icon, NoteFilterModal, SearchFilter, Badge, TextInput, DateInput, SelectInput, Pagination},
  layout: Layout,
  metaInfo: {
    title: 'Dispatch Notes',
  },

  props: {
    notes: Object,
    filters: Object,
    locations: Object,
    teams: Array,
    warehouses: Array,
    vesselLocations: Object,
    vessels: Array,
    statCards: Object,
    isAdmin: Boolean,
  },

  data() {
    return {
      form: {
        search: this.filters.search || null,
        team: this.filters.team || null,
        warehouse: this.filters.warehouse || null,
        location: this.filters.location || null,
        boat: this.filters.boat || null,
        delivery_date: this.filters.delivery_date || null,
        status: this.filters.status || null,
        method: this.filters.method || null,
      },

      activeNote: null,
    }
  },
  computed: {
    deliveryTeams() {
      return teams => {
        if (Array.isArray(teams) && teams.length) {
          return teams.map(itm => itm.name).join(',')
        }
        return 'N/A'
      }
    },
    hasFilter() {
      return Object.keys(this.filters).some(
        key => (key !== 'status' || this.filters['status'] !== 'pending')
          && this.filters[key] !== null
          && this.filters[key] !== ''
      );
    },
    hasNoteExpended() {
      return note => {

        if (this.activeNote && Object.keys(this.activeNote).length) {
          return this.activeNote.id === note.id
        }
        return false;
      }
    }
  },

  methods: {
    toggleNote(note) {

      if (this.hasNoteExpended(note)) {
        this.activeNote = null
      } else {
        this.activeNote = note
      }
    },
    reset() {
      this.form = mapValues(this.form, () => null)
      this.form.status = 'pending'
      this.applyFilter()
    },
    applyFilter() {
      let query = pickBy(this.form)
      this.$inertia.get(this.route('delivery-notes.index', Object.keys(query).length ? query : {remmber: 'forget'}))
    },
    toggleStatus(status) {
      this.form.status = status
      this.applyFilter()
    },
    toggleType(type) {
      this.form.method = type
      this.applyFilter()
    },
  },
}
</script>
